import type { DirectoryElements, DirectoryPublic } from '@repo/types/directory';
import type { FilePublicWithPermissions } from '@repo/types/files';

import { useGetDirectoryWithShared } from '@repo/api/file-system/useGetDirectoryWithShared';
import { getIsFSEntityFile } from '@repo/lib/fs/getIsFSEntityFile';
import { CircleCheck, LoaderCircle } from 'lucide-react';
import { useState } from 'react';

import type { DirectoryBaseConfig, FileUploadedCallback, FileUploadErrorCallback, UploadFileFn } from '@/hooks/file-upload';

import FsItemSelection from '@/components/features/files/select-fs-items/FsItemSelection';
import FsUploadFiles from '@/components/features/files/select-fs-items/FsUploadFile';
import FsItemsUploadList from '@/components/features/files/select-fs-items/SelectFsItemsUploadList';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { LoadingSwap } from '@/components/ui/loading-swap';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useFileUpload } from '@/hooks/file-upload';
import { useFsTypeNavigation, useSelectFsItems } from '@/hooks/fs';

interface SelectFsItemsOptions<R extends DirectoryBaseConfig> {
  allow: { file?: boolean; directory?: boolean };
  multiple?: boolean;
  accept?: string;
  maxFiles?: number;
  maxSize?: number;
  uploadFileFn?: UploadFileFn<R>;
  onFileUploaded?: FileUploadedCallback;
  onFileUploadError?: FileUploadErrorCallback<R>;
  selectedCallbackFn?: (selected: DirectoryElements | null) => Promise<void>;
}

interface SelectFsItemsDialogProps<R extends DirectoryBaseConfig> {
  defaultDirectoryId?: string;
  defaultSelected?: DirectoryElements;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  options?: SelectFsItemsOptions<R>;
}

export default function SelectFsItemsDialog<R extends DirectoryBaseConfig>(selectFsItemsDialogProps: SelectFsItemsDialogProps<R>) {
  const { open, onOpenChange, defaultDirectoryId, defaultSelected, options: incomingOptions } = selectFsItemsDialogProps;

  const {
    allow = { file: true, directory: false },
    multiple = false,
    maxFiles = Number.POSITIVE_INFINITY,
    maxSize = 1000000 * 500,
    accept = '*/*',
    uploadFileFn,
    onFileUploaded,
    onFileUploadError,
    selectedCallbackFn,
  } = incomingOptions || {};
  // Local state for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [fsUploadState, fsUploadActions] = useFileUpload<DirectoryBaseConfig, R>({
    maxFiles: maxFiles,
    maxSize: maxSize,
    postponeUpload: true,
    onFileUploaded: onFileUploaded,
    onFileUploadError: onFileUploadError,
    uploadFileFn: async (file, options) => {
      await uploadFileFn?.(file, options);
    },
  });

  const tabsNavigation = useFsTypeNavigation({
    defaultTab: 'MY_DRIVE',
    defaultDirectoryId,
    onTabChangeCallback: (tab) => {
      if (tab === 'UPLOAD') {
        selectActions.setSelected(null);
        fsUploadActions.clearFiles();
      } else {
        fsUploadActions.clearFiles();
      }
    },
  });

  const getData = useGetDirectoryWithShared({
    directoryId: tabsNavigation.state.directoryId,
    useSharedEndpoint: tabsNavigation.state.useSharedEndpoint,
    enabled: open,
  });

  const { state: selectState, actions: selectActions } = useSelectFsItems({
    defaultSelected,
    items: [...getData.fsItems.dirs, ...getData.fsItems.files],
    multiple: multiple,
    fsSelectMode: 'basic',
    allow: allow,
    accept: accept,
  });

  const onClose = () => {
    onOpenChange(false);
    setIsLoading(false);
    selectActions.setSelected(null);
    fsUploadActions.clearFiles();
    tabsNavigation.actions.reset();
  };

  const onCancel = () => {
    if (isLoading) {
      fsUploadActions.abortAllUploads();
    }
    onClose();
  };

  const onSubmit = async (overrideSelection?: DirectoryElements | null) => {
    setIsLoading(true);
    if (tabsNavigation.state.tab === 'UPLOAD' && fsUploadState.files.length > 0) {
      await fsUploadActions.uploadAll?.();
      onClose();
    } else {
      const selection = overrideSelection ?? selectState.selected;
      await selectedCallbackFn?.(selection);
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-[85vh] sm:max-w-2xl" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={() => onClose()} isClosable={false}>
        <Tabs className="relative grow" value={tabsNavigation.state.tab} onValueChange={tabsNavigation.actions.handleTabChange} asChild>
          <DialogBody>
            <DialogHeader className={'border-border-primary border-b'}>
              <DialogTitle className="sr-only">Wybierz z listy bądź prześlij plik</DialogTitle>
              <TabsList className={'p-0.5 text-sm'}>
                <TabsTrigger className="px-1.5 sm:px-3" value="MY_DRIVE" disabled={isLoading}>
                  Mój dysk
                </TabsTrigger>
                <TabsTrigger className="px-1.5 sm:px-3" value="SHARED_DRIVE" disabled={isLoading}>
                  Dysk współdzielony
                </TabsTrigger>
                <TabsTrigger className="px-1.5 sm:px-3" value="UPLOAD" disabled={isLoading}>
                  Prześlij
                </TabsTrigger>
              </TabsList>
              <DialogDescription className={'sr-only'}>Wybierz element z systemu plików, lub prześlij nowy plik</DialogDescription>
            </DialogHeader>
            <div className={'relative grow overflow-auto p-4'}>
              {isLoading && (
                <div className="bg-surface-primary/10 absolute inset-0 z-10 p-4 backdrop-blur-sm">
                  {tabsNavigation.state.tab === 'UPLOAD' ? (
                    <FsItemsUploadList
                      files={fsUploadState.files}
                      directories={fsUploadState.directories}
                      abortDirectoryUpload={fsUploadActions.abortDirectoryUpload}
                      abortFileUpload={fsUploadActions.abortFileUpload}
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center gap-2">
                      <LoaderCircle className={'animate-spin'} aria-hidden={'true'} />
                      <h5 className={'font-medium'}>Trwa zapisywanie...</h5>
                    </div>
                  )}
                </div>
              )}
              <TabsContent value="MY_DRIVE">
                <FsItemSelection
                  fsType={'MY_DRIVE'}
                  state={selectState}
                  actions={selectActions}
                  queryData={getData}
                  handleNavigateTo={tabsNavigation.actions.handleNavigateTo}
                  multiple={multiple}
                  onDoubleClickSelection={async (item) => {
                    const isFile = getIsFSEntityFile(item);
                    const selection: DirectoryElements = {
                      files: isFile ? [item as FilePublicWithPermissions] : [],
                      directories: !isFile ? [item as DirectoryPublic] : [],
                    };
                    await onSubmit(selection);
                  }}
                />
              </TabsContent>
              <TabsContent value="SHARED_DRIVE">
                <FsItemSelection
                  fsType={'SHARED_DRIVE'}
                  state={selectState}
                  actions={selectActions}
                  queryData={getData}
                  handleNavigateTo={tabsNavigation.actions.handleNavigateTo}
                  multiple={multiple}
                  onDoubleClickSelection={async (item) => {
                    const isFile = getIsFSEntityFile(item);
                    const selection: DirectoryElements = {
                      files: isFile ? [item as FilePublicWithPermissions] : [],
                      directories: !isFile ? [item as DirectoryPublic] : [],
                    };
                    await onSubmit(selection);
                  }}
                />
              </TabsContent>
              <TabsContent value="UPLOAD" className="h-full">
                <FsUploadFiles
                  options={{
                    allow: allow,
                    maxSize: maxSize,
                    accept: accept,
                    multiple: multiple,
                  }}
                  state={fsUploadState}
                  actions={fsUploadActions}
                />
              </TabsContent>
            </div>
          </DialogBody>
        </Tabs>
        <DialogFooter className={'border-border-primary border-t'}>
          <Button type={'button'} variant={'tertiary'} className={'w-full sm:w-fit'} onClick={onCancel}>
            Anuluj
          </Button>
          <Button type="submit" onClick={() => onSubmit()} disabled={isLoading} className={'w-full sm:w-fit'}>
            <LoadingSwap isLoading={isLoading} className="flex items-center gap-2">
              <CircleCheck />
              Potwierdź
            </LoadingSwap>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
